import { clampImageWidth } from "@corca/tiptap";
import { NodeSelection } from "@tiptap/pm/state";

import {
  blockInsertionPoint,
  insertWithUploadPlaceholder,
} from "@/components/tiptap/UploadPlaceholder";
import {
  addPendingImagePreview,
  releasePendingImagePreview,
} from "@/components/tiptap/utils/pendingImagePreviews";
import { uploadImage } from "@/components/tiptap/utils/uploadImage";
import { getImageDimensions } from "@/utils/getImageDimensions";
import { validateImageFile } from "@/utils/validateImageFile";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { EditorView } from "@tiptap/pm/view";

/**
 * Inserts an image at `position` (mapped to the nearest block boundary).
 * Raster images show a local preview at their final size right away and
 * upload in the background, swapping in the stored image once ready so
 * nothing shifts; SVGs are sanitized into a data URI locally — nothing to
 * upload, so they're inserted as soon as they're ready. `select` selects the
 * inserted image, which surfaces its Recognize button.
 */
export async function insertImage(
  view: EditorView,
  position: number,
  file: File,
  scope: UploadScope,
  { select = false }: { select?: boolean } = {},
): Promise<void> {
  const fileType = validateImageFile(file);
  if (!fileType) return;

  const { image } = view.state.schema.nodes;

  if (fileType === "image/svg+xml") {
    const attrs = await uploadImage(file, scope);
    if (attrs && !view.isDestroyed) {
      const insertAt = blockInsertionPoint(view.state, position, image);
      const transaction = view.state.tr.insert(insertAt, image.create(attrs));
      if (select) {
        transaction.setSelection(NodeSelection.create(transaction.doc, insertAt));
      }
      view.dispatch(transaction);
    }
    return;
  }

  const controller = new AbortController();
  const upload = uploadImage(file, scope, { signal: controller.signal }).catch((error) => {
    if (!controller.signal.aborted) {
      console.error("Failed to upload file", error);
    }
    return null;
  });

  const url = URL.createObjectURL(file);
  const dimensions = await getImageDimensions(url);
  const width = dimensions ? clampImageWidth(dimensions.width) : undefined;
  const height = width && dimensions ? (width * dimensions.height) / dimensions.width : undefined;

  let pendingKey: string | undefined;
  const inserted = await insertWithUploadPlaceholder(
    view,
    image,
    position,
    file,
    () => upload,
    (attrs) => {
      if ("key" in attrs) {
        pendingKey = attrs.key;
        addPendingImagePreview(attrs.key, url);
      }
      return image.create({ ...attrs, width, height });
    },
    { preview: { url, width, height }, controller, select },
  );

  if (!inserted) {
    if (pendingKey) {
      releasePendingImagePreview(pendingKey);
    } else {
      URL.revokeObjectURL(url);
    }
  }
}
